import { Component, Input } from '@angular/core';

@Component({
  selector: 'ngx-skeleton-loader',
  template: `
    <div *ngFor="let item of items; trackBy: trackByIndex" class="skeleton-loader-stack">
      <div
        class="skeleton-loader"
        [class.skeleton-loader--circle]="appearance === 'circle'"
        [class.skeleton-loader--square]="appearance === 'square'"
        [ngClass]="cssClass"
        [style.width]="resolvedWidth"
        [style.height]="resolvedHeight"
        [style.borderRadius]="resolvedBorderRadius"
        [style.marginRight]="resolvedMarginRight"
        [attr.aria-label]="'Loading'"
        aria-busy="true"
      ></div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .skeleton-loader-stack {
        display: block;
        width: 100%;
        margin-bottom: 8px;
      }

      .skeleton-loader {
        position: relative;
        overflow: hidden;
        display: block;
        width: 100%;
        height: 16px;
        border-radius: 8px;
        background: linear-gradient(90deg, #ececec 25%, #f5f5f5 50%, #ececec 75%);
        background-size: 200% 100%;
        animation: skeleton-shimmer 1.2s infinite linear;
      }

      .skeleton-loader--circle {
        border-radius: 50%;
      }

      .skeleton-loader--square {
        border-radius: 4px;
      }

      @keyframes skeleton-shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
    `
  ]
})
export class SkeletonLoaderComponent {
  @Input() count: number | string = 1;
  @Input() appearance: 'circle' | 'square' | 'line' = 'line';
  @Input() size: string | number;
  @Input() theme: Record<string, string | number> = {};
  @Input('class') cssClass: string | string[] | null = null;

  get items(): number[] {
    const itemCount = Number(this.count) || 1;
    return Array.from({ length: Math.max(1, itemCount) }, (_, index) => index);
  }

  trackByIndex(index: number): number {
    return index;
  }

  get resolvedWidth(): string | null {
    const width = this.theme['width.px'] ?? this.theme.width ?? this.size ?? '100%';
    return this.toCssValue(width);
  }

  get resolvedHeight(): string | null {
    const height = this.theme['height.px'] ?? this.theme.height ?? this.size ?? '16px';
    return this.toCssValue(height);
  }

  get resolvedBorderRadius(): string | null {
    const radius = this.theme['border-radius.px'] ?? this.theme['borderRadius.px'] ?? this.theme.borderRadius ?? (this.appearance === 'circle' ? '50%' : '8px');
    return this.toCssValue(radius);
  }

  get resolvedMarginRight(): string | null {
    const marginRight = this.theme['margin-right.px'] ?? this.theme['margin-right'] ?? this.theme.marginRight ?? null;
    return marginRight == null ? null : this.toCssValue(marginRight);
  }

  private toCssValue(value: string | number | null): string | null {
    if (value == null) {
      return null;
    }

    return typeof value === 'number' ? `${value}px` : String(value);
  }
}
